import React, { useState, useEffect, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import DashboardLayout from '@/components/DashboardLayout';
import FloatingFilterPanel from '@/components/FloatingFilterPanel';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { formatDate, formatUSD } from '@/utils/formatters';
import { exportToPDF } from '@/utils/exportToPDF';
import { Upload, Plus, Download, FileSpreadsheet, Pencil, Trash2, AlertTriangle, Search, AlertCircle, Settings2, X, Check, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, FileDown, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';
import { parseISO, isValid, isWithinInterval, parse } from 'date-fns';

// Column definitions with labels
const COLUMN_DEFS = {
  po_number: { label: 'PO Number', width: 'w-28' },
  po_status: { label: 'PO Status', width: 'w-24' },
  qty: { label: 'Qty', width: 'w-20', align: 'right' },
  rate: { label: 'Rate', width: 'w-20', align: 'right' },
  uom: { label: 'UOM', width: 'w-24' },
  currency: { label: 'Currency', width: 'w-20' },
  amount: { label: 'Amount', width: 'w-28', align: 'right' },
  file_received_on: { label: 'Delivered On', width: 'w-28' },
  due_on: { label: 'Due On', width: 'w-28' },
  accepted_on: { label: 'Accepted On', width: 'w-28' },
  rejected_on: { label: 'Rejected On', width: 'w-28' },
  target_language: { label: 'Target Language', width: 'w-28' },
  source_language: { label: 'Source Language', width: 'w-28' },
  task: { label: 'Task', width: 'w-28' },
  owning_office: { label: 'Owning Office', width: 'w-28' },
  job_id: { label: 'Job ID', width: 'w-28' },
  language_attribute: { label: 'Language Attr', width: 'w-28' },
  main_title: { label: 'Main Title', width: 'w-40' },
  episode_title: { label: 'Episode Title', width: 'w-32' },
  episode: { label: 'Episode', width: 'w-20' },
  client: { label: 'Client', width: 'w-32' },
  external_client: { label: 'External Client', width: 'w-32' },
  program_number: { label: 'Program #', width: 'w-28' },
  po_created_by: { label: 'PO Created By', width: 'w-28' },
  supervisor_flag: { label: 'Supervisor Flag', width: 'w-28' },
  resource_acceptance_status: { label: 'Acceptance Status', width: 'w-32' }
};

const DEFAULT_VISIBLE = ['po_number', 'po_status', 'amount', 'client', 'main_title', 'task', 'due_on', 'accepted_on'];
const PAGE_SIZE_OPTIONS = [50, 100, 200, 500, 1000];

// Status badge colors
const getStatusBadge = (status) => {
  const s = (status || '').toLowerCase();
  if (s === 'accepted') return <Badge className="bg-green-500 text-white">Accepted</Badge>;
  if (s === 'completed') return <Badge className="bg-blue-500 text-white">Completed</Badge>;
  if (s === 'issued') return <Badge className="bg-yellow-500 text-black">Issued</Badge>;
  if (s === 'canceled' || s === 'cancelled') return <Badge className="bg-red-500 text-white">Canceled</Badge>;
  return <Badge variant="outline">{status || '-'}</Badge>;
};

// Parse date from various formats
const parseAnyDate = (dateStr) => {
  if (!dateStr) return null;
  
  // Try ISO format first
  let date = parseISO(dateStr);
  if (isValid(date)) return date;
  
  // Try common formats
  const formats = [
    'dd/MM/yyyy',
    'dd-MM-yyyy',
    'MM/dd/yyyy',
    'yyyy-MM-dd',
    'dd/MM/yyyy, HH:mm:ss',
    'dd-MM-yyyy HH:mm:ss'
  ];
  
  for (const fmt of formats) {
    try {
      date = parse(dateStr, fmt, new Date());
      if (isValid(date)) return date;
    } catch {}
  }
  
  // Try native Date parsing as fallback
  date = new Date(dateStr);
  return isValid(date) ? date : null;
};

const ClientBillings = () => {
  const { user } = useOutletContext();
  const [clientBillings, setClientBillings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showManualDialog, setShowManualDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showDeleteAllDialog, setShowDeleteAllDialog] = useState(false);
  const [showColumnDialog, setShowColumnDialog] = useState(false);
  const [uploadType, setUploadType] = useState('excel');
  const [file, setFile] = useState(null);
  const [googleSheetUrl, setGoogleSheetUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingBilling, setEditingBilling] = useState(null);
  const [deletingBilling, setDeletingBilling] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [visibleColumns, setVisibleColumns] = useState(DEFAULT_VISIBLE);
  const [tempVisibleColumns, setTempVisibleColumns] = useState(DEFAULT_VISIBLE);
  
  // Date filter state
  const [dateFilter, setDateFilter] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  
  const [manualForm, setManualForm] = useState({
    po_number: '', po_status: 'issued', qty: '', rate: '', uom: '', currency: 'USD', amount: '',
    file_received_on: '', due_on: '', accepted_on: '', rejected_on: '',
    target_language: '', source_language: '', task: '', owning_office: '', job_id: '',
    language_attribute: '', main_title: '', episode_title: '', episode: '',
    client: '', external_client: '', program_number: '', po_created_by: '',
    supervisor_flag: '', resource_acceptance_status: ''
  });
  
  useEffect(() => {
    loadData();
    loadColumnSettings();
  }, []);
  
  // Filter billings based on search and date filter
  const filteredBillings = useMemo(() => {
    let result = [...clientBillings];
    
    // Month filter based on due_on
    if (selectedMonth !== 'all') {
      const [fy, fm] = selectedMonth.split('-').map(Number);
      result = result.filter(b => {
        const d = b.due_on || b.file_received_on || '';
        if (!d) return false;
        const parsed = parseAnyDate(d);
        if (!parsed) return false;
        return parsed.getFullYear() === fy && (parsed.getMonth() + 1) === fm;
      });
    }
    
    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(b => 
        b.main_title?.toLowerCase().includes(term) ||
        b.client?.toLowerCase().includes(term) ||
        b.po_number?.toLowerCase().includes(term) ||
        b.task?.toLowerCase().includes(term) ||
        b.job_id?.toLowerCase().includes(term)
      );
    }
    
    // Apply date filter
    if (dateFilter && dateFilter.startDate && dateFilter.endDate) {
      result = result.filter(b => {
        const dateValue = b[dateFilter.dateField];
        if (!dateValue) return false;
        
        const parsedDate = parseAnyDate(dateValue);
        if (!parsedDate) return false;
        
        return isWithinInterval(parsedDate, {
          start: dateFilter.startDate,
          end: dateFilter.endDate
        });
      });
    }
    
    return result;
  }, [clientBillings, searchTerm, dateFilter]);
  
  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, dateFilter, pageSize]);
  
  // Pagination calculations
  const totalPages = Math.ceil(filteredBillings.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedBillings = filteredBillings.slice(startIndex, endIndex);
  
  const loadData = async () => {
    try {
      const response = await api.get('/owner/client-billings');
      setClientBillings(response.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load billing data');
    } finally {
      setLoading(false);
    }
  };
  
  const loadColumnSettings = async () => {
    try {
      const response = await api.get('/owner/billing-columns');
      setVisibleColumns(response.data.visible_columns || DEFAULT_VISIBLE);
      setTempVisibleColumns(response.data.visible_columns || DEFAULT_VISIBLE);
    } catch (error) {
      console.error('Error loading column settings:', error);
    }
  };
  
  const saveColumnSettings = async () => {
    try {
      await api.put('/owner/billing-columns', { visible_columns: tempVisibleColumns });
      setVisibleColumns(tempVisibleColumns);
      setShowColumnDialog(false);
      toast.success('Column settings saved!');
    } catch (error) {
      toast.error('Failed to save column settings');
    }
  };
  
  // Calculate stats based on filtered data
  const stats = useMemo(() => {
    const unaccepted = filteredBillings.filter(b => !b.resource_acceptance_status);
    const totalAmount = filteredBillings.reduce((sum, b) => sum + (b.amount || 0), 0);
    const unacceptedAmount = unaccepted.reduce((sum, b) => sum + (b.amount || 0), 0);
    return {
      total: filteredBillings.length,
      totalAmount,
      unacceptedCount: unaccepted.length,
      unacceptedAmount
    };
  }, [filteredBillings]);
  
  const handleFileUpload = async (e) => {
    e.preventDefault();
    setUploading(true);
    
    try {
      if (uploadType === 'excel') {
        if (!file) {
          toast.error('Please select an Excel file');
          setUploading(false);
          return;
        }
        
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await api.post('/owner/upload-billings-excel', formData, { timeout: 60000 });
        
        toast.success(`Imported ${response.data.count} billing records!`);
        setShowUploadDialog(false);
        setFile(null);
        await loadData();
      } else {
        if (!googleSheetUrl) {
          toast.error('Please enter Google Sheets URL');
          return;
        }
        
        const response = await api.post('/owner/import-google-sheet', { sheet_url: googleSheetUrl });
        toast.success(`Imported ${response.data.count} records!`);
        setShowUploadDialog(false);
        setGoogleSheetUrl('');
        await loadData();
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to import data');
    } finally {
      setUploading(false);
    }
  };
  
  const handleManualAdd = async (e) => {
    e.preventDefault();
    try {
      await api.post('/owner/client-billings', manualForm);
      toast.success('Billing record added!');
      setShowManualDialog(false);
      setManualForm({
        po_number: '', po_status: 'issued', qty: '', rate: '', uom: '', currency: 'USD', amount: '',
        file_received_on: '', due_on: '', accepted_on: '', rejected_on: '',
        target_language: '', source_language: '', task: '', owning_office: '', job_id: '',
        language_attribute: '', main_title: '', episode_title: '', episode: '',
        client: '', external_client: '', program_number: '', po_created_by: '',
        supervisor_flag: '', resource_acceptance_status: ''
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add record');
    }
  };
  
  const handleEditClick = (billing) => {
    setEditingBilling(billing);
    setEditForm({ ...billing });
    setShowEditDialog(true);
  };
  
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/owner/client-billings/${editingBilling.billing_id}`, editForm);
      toast.success('Record updated!');
      setShowEditDialog(false);
      setEditingBilling(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    }
  };
  
  const handleDeleteClick = (billing) => {
    setDeletingBilling(billing);
    setShowDeleteDialog(true);
  };
  
  const handleDeleteConfirm = async () => {
    try {
      await api.delete(`/owner/client-billings/${deletingBilling.billing_id}`);
      toast.success('Record deleted!');
      setShowDeleteDialog(false);
      setDeletingBilling(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };
  
  const handleDeleteAllConfirm = async () => {
    try {
      await api.delete('/owner/client-billings');
      toast.success('All records deleted!');
      setShowDeleteAllDialog(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };
  
  const downloadTemplate = () => {
    const headers = Object.keys(COLUMN_DEFS).map(k => COLUMN_DEFS[k].label);
    const csv = headers.join(',') + '\n' + 
      'PO-001,issued,45.5,50,runtime,USD,2275,2026-01-10,2026-01-15,,,Hindi,English,Translation,Mumbai,JOB-123,,Web Series,Episode 1,1,ABC Studios,Netflix,PRG-001,John Doe,OK,';
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'client_billings_template.csv';
    a.click();
  };
  
  const renderCellValue = (billing, col) => {
    const value = billing[col];
    
    if (col === 'po_status') return getStatusBadge(value);
    if (col === 'amount') return <span className="font-semibold text-success">{formatUSD(value || 0)}</span>;
    if (col === 'rate' || col === 'qty') return value ? value.toFixed(2) : '-';
    if (col === 'resource_acceptance_status') {
      if (!value) return <Badge variant="outline" className="text-orange-500 border-orange-500"><AlertCircle className="w-3 h-3 mr-1" />Pending</Badge>;
      if (value.toLowerCase() === 'accepted') return <Badge className="bg-green-500 text-white"><Check className="w-3 h-3 mr-1" />Accepted</Badge>;
      return <Badge variant="outline">{value}</Badge>;
    }
    if (col === 'supervisor_flag') {
      if (!value) return '-';
      if (value.toLowerCase() === 'ok') return <Badge className="bg-green-100 text-green-700">OK</Badge>;
      return <Badge className="bg-orange-100 text-orange-700">Flagged</Badge>;
    }
    if (['file_received_on', 'due_on', 'accepted_on', 'rejected_on'].includes(col)) {
      return value ? formatDate(value) : '-';
    }
    
    return value || '-';
  };
  
  const toggleColumn = (col) => {
    setTempVisibleColumns(prev => 
      prev.includes(col) ? prev.filter(c => c !== col) : [...prev, col]
    );
  };
  
  // Pagination handlers
  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };
  
  const handlePageSizeChange = (value) => {
    setPageSize(Number(value));
    setCurrentPage(1);
  };
  
  const handleExportPDF = async () => {
    toast.info('Generating PDF...');
    const success = await exportToPDF('client-billings-content', `client-billings-${new Date().toISOString().split('T')[0]}.pdf`);
    if (success) {
      toast.success('PDF downloaded successfully!');
    } else {
      toast.error('Failed to generate PDF');
    }
  };
  
  return (
    <DashboardLayout user={user}>
      <div id="client-billings-content" className="space-y-6">
        <motion.div 
          className="flex items-center justify-between"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <div>
            <h1 className="text-4xl font-heading font-bold accent-gradient mb-2">Client Billings</h1>
            <p className="text-muted-foreground">Manage and import client billing records</p>
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            <Select value={selectedMonth} onValueChange={(v) => { setSelectedMonth(v); setCurrentPage(1); }}>
              <SelectTrigger className="w-40 h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Months</SelectItem>
                {Array.from({ length: 12 }, (_, i) => { const d = new Date(); d.setMonth(d.getMonth() - i); const v = d.toISOString().slice(0, 7); return <SelectItem key={v} value={v}>{d.toLocaleDateString('en-US', { year: 'numeric', month: 'short' })}</SelectItem>; })}
              </SelectContent>
            </Select>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button onClick={handleExportPDF} variant="outline" size="sm" className="bg-gradient-to-r from-rose-50 to-pink-50 border-rose-200 text-rose-600 hover:from-rose-100 hover:to-pink-100 active:bg-red-600 active:text-white active:border-red-600 active:from-red-600 active:to-red-600" data-testid="billings-export-pdf-btn">
                <FileDown className="w-4 h-4 mr-2" />
                Export PDF
              </Button>
            </motion.div>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button onClick={() => { setTempVisibleColumns(visibleColumns); setShowColumnDialog(true); }} variant="outline" size="sm">
                <Settings2 className="w-4 h-4 mr-2" />
                Columns
              </Button>
            </motion.div>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button onClick={downloadTemplate} variant="outline" data-testid="btn-download-template">
                <Download className="w-4 h-4 mr-2" />
                Template
              </Button>
            </motion.div>
            <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
              <DialogTrigger asChild>
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button data-testid="btn-import" className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700">
                    <Upload className="w-4 h-4 mr-2" />
                    Import
                  </Button>
                </motion.div>
              </DialogTrigger>
              <DialogContent className="max-w-xl">
                <DialogHeader>
                  <DialogTitle>Import Client Billings</DialogTitle>
                </DialogHeader>
                <Tabs value={uploadType} onValueChange={setUploadType}>
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="excel">Excel Upload</TabsTrigger>
                    <TabsTrigger value="google">Google Sheets</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="excel" className="space-y-4">
                    <form onSubmit={handleFileUpload}>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label>Upload Excel File (.xlsx, .xls, .csv)</Label>
                          <Input type="file" accept=".xlsx,.xls,.csv" onChange={(e) => setFile(e.target.files[0])} />
                        </div>
                        <Button type="submit" className="w-full" disabled={uploading}>
                          {uploading ? 'Uploading...' : 'Upload'}
                        </Button>
                      </div>
                    </form>
                  </TabsContent>
                  
                  <TabsContent value="google" className="space-y-4">
                    <form onSubmit={handleFileUpload}>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label>Google Sheets URL</Label>
                          <Input placeholder="https://docs.google.com/spreadsheets/d/..." value={googleSheetUrl} onChange={(e) => setGoogleSheetUrl(e.target.value)} />
                        </div>
                        <Button type="submit" className="w-full" disabled={uploading}>
                          {uploading ? 'Importing...' : 'Import'}
                        </Button>
                      </div>
                    </form>
                  </TabsContent>
                </Tabs>
              </DialogContent>
            </Dialog>
            <Dialog open={showManualDialog} onOpenChange={setShowManualDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="btn-add-manual">
                  <Plus className="w-4 h-4 mr-2" />
                  Add
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Add Billing Record</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleManualAdd} className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>PO Number *</Label>
                      <Input value={manualForm.po_number} onChange={(e) => setManualForm({...manualForm, po_number: e.target.value})} required />
                    </div>
                    <div className="space-y-2">
                      <Label>PO Status</Label>
                      <Select value={manualForm.po_status} onValueChange={(v) => setManualForm({...manualForm, po_status: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="issued">Issued</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                          <SelectItem value="accepted">Accepted</SelectItem>
                          <SelectItem value="canceled">Canceled</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Amount</Label>
                      <Input type="number" step="0.01" value={manualForm.amount} onChange={(e) => setManualForm({...manualForm, amount: e.target.value})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Qty</Label>
                      <Input type="number" step="0.01" value={manualForm.qty} onChange={(e) => setManualForm({...manualForm, qty: e.target.value})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Rate</Label>
                      <Input type="number" step="0.01" value={manualForm.rate} onChange={(e) => setManualForm({...manualForm, rate: e.target.value})} />
                    </div>
                    <div className="space-y-2">
                      <Label>UOM</Label>
                      <Input value={manualForm.uom} onChange={(e) => setManualForm({...manualForm, uom: e.target.value})} placeholder="runtime/flat" />
                    </div>
                    <div className="space-y-2">
                      <Label>Currency</Label>
                      <Select value={manualForm.currency} onValueChange={(v) => setManualForm({...manualForm, currency: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="USD">USD</SelectItem>
                          <SelectItem value="EUR">EUR</SelectItem>
                          <SelectItem value="GBP">GBP</SelectItem>
                          <SelectItem value="INR">INR</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Client *</Label>
                      <Input value={manualForm.client} onChange={(e) => setManualForm({...manualForm, client: e.target.value})} required />
                    </div>
                    <div className="space-y-2">
                      <Label>External Client</Label>
                      <Input value={manualForm.external_client} onChange={(e) => setManualForm({...manualForm, external_client: e.target.value})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Main Title</Label>
                      <Input value={manualForm.main_title} onChange={(e) => setManualForm({...manualForm, main_title: e.target.value})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Episode Title</Label>
                      <Input value={manualForm.episode_title} onChange={(e) => setManualForm({...manualForm, episode_title: e.target.value})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Episode #</Label>
                      <Input value={manualForm.episode} onChange={(e) => setManualForm({...manualForm, episode: e.target.value})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Task</Label>
                      <Input value={manualForm.task} onChange={(e) => setManualForm({...manualForm, task: e.target.value})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Target Language</Label>
                      <Input value={manualForm.target_language} onChange={(e) => setManualForm({...manualForm, target_language: e.target.value})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Source Language</Label>
                      <Input value={manualForm.source_language} onChange={(e) => setManualForm({...manualForm, source_language: e.target.value})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Due On</Label>
                      <Input type="date" value={manualForm.due_on} onChange={(e) => setManualForm({...manualForm, due_on: e.target.value})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Delivered On</Label>
                      <Input type="date" value={manualForm.file_received_on} onChange={(e) => setManualForm({...manualForm, file_received_on: e.target.value})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Accepted On</Label>
                      <Input type="date" value={manualForm.accepted_on} onChange={(e) => setManualForm({...manualForm, accepted_on: e.target.value})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Job ID</Label>
                      <Input value={manualForm.job_id} onChange={(e) => setManualForm({...manualForm, job_id: e.target.value})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Owning Office</Label>
                      <Input value={manualForm.owning_office} onChange={(e) => setManualForm({...manualForm, owning_office: e.target.value})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Supervisor Flag</Label>
                      <Select value={manualForm.supervisor_flag} onValueChange={(v) => setManualForm({...manualForm, supervisor_flag: v})}>
                        <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="OK">OK</SelectItem>
                          <SelectItem value="Flagged">Flagged</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Acceptance Status</Label>
                      <Select value={manualForm.resource_acceptance_status || 'pending'} onValueChange={(v) => setManualForm({...manualForm, resource_acceptance_status: v === 'pending' ? '' : v})}>
                        <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Accepted">Accepted</SelectItem>
                          <SelectItem value="pending">Pending</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <Button type="submit" className="w-full bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700">Add Record</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </motion.div>
        
        {/* Reminder Banner */}
        <AnimatePresence>
          {stats.unacceptedCount > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <Card className="border-l-4 border-l-orange-500 bg-gradient-to-r from-orange-50 to-amber-50 shadow-lg shadow-orange-100">
                <CardContent className="py-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <motion.div
                      animate={{ scale: [1, 1.1, 1] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      <AlertCircle className="w-6 h-6 text-orange-500" />
                    </motion.div>
                    <div>
                      <p className="font-semibold text-orange-800">
                        {stats.unacceptedCount} invoice{stats.unacceptedCount > 1 ? 's' : ''} pending acceptance
                      </p>
                      <p className="text-sm text-orange-600">
                        Total unaccepted amount: {formatUSD(stats.unacceptedAmount)}
                      </p>
                    </div>
                  </div>
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Button variant="outline" className="border-orange-400 text-orange-600 hover:bg-orange-100" onClick={() => setSearchTerm('')}>
                      View All
                    </Button>
                  </motion.div>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Filter Status Bar */}
        <AnimatePresence>
          {dateFilter && (
            <motion.div
              className="flex items-center gap-2 text-sm px-4 py-2.5 bg-gradient-to-r from-violet-50 to-purple-50 border border-violet-200 rounded-xl"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
            >
              <Sparkles className="w-4 h-4 text-violet-500" />
              <span className="text-violet-700">
                Filter active: Showing <strong>{filteredBillings.length}</strong> of {clientBillings.length} records
              </span>
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Search and Stats */}
        <motion.div 
          className="flex items-center gap-4 flex-wrap"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by title, client, PO number, job ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 rounded-xl border-gray-200 focus:ring-violet-500"
              data-testid="search-input"
            />
          </div>
          <div className="text-sm text-muted-foreground">
            Showing <strong>{paginatedBillings.length}</strong> of {filteredBillings.length} records
            {clientBillings.length !== filteredBillings.length && (
              <span className="text-xs ml-1">({clientBillings.length} total)</span>
            )}
          </div>
          <div className="text-sm font-semibold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
            Total: {formatUSD(stats.totalAmount)}
          </div>
        </motion.div>
        
        {/* Table */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="shadow-lg rounded-xl overflow-hidden">
            <CardContent className="p-0">
              {loading ? (
                <div className="text-center py-12">
                  <motion.div 
                    className="w-12 h-12 border-4 border-violet-500 border-t-transparent rounded-full mx-auto mb-4"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  />
                  <p className="text-muted-foreground">Loading...</p>
                </div>
              ) : filteredBillings.length === 0 ? (
                <div className="text-center py-12">
                  <FileSpreadsheet className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground mb-4">
                    {searchTerm || dateFilter ? 'No matching records found' : 'No billing records yet'}
                  </p>
                  {!searchTerm && !dateFilter && (
                    <Button onClick={() => setShowUploadDialog(true)} className="bg-gradient-to-r from-violet-500 to-purple-600">
                      <Upload className="w-4 h-4 mr-2" />
                      Import Data
                    </Button>
                  )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {visibleColumns.map(col => (
                        <TableHead key={col} className={COLUMN_DEFS[col]?.align === 'right' ? 'text-right' : ''}>
                          {COLUMN_DEFS[col]?.label}
                        </TableHead>
                      ))}
                      <TableHead className="text-center w-20">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedBillings.map((billing) => (
                      <TableRow key={billing.billing_id} className={!billing.resource_acceptance_status ? 'bg-orange-50/50' : ''}>
                        {visibleColumns.map(col => (
                          <TableCell key={col} className={`${COLUMN_DEFS[col]?.width} ${COLUMN_DEFS[col]?.align === 'right' ? 'text-right' : ''}`}>
                            {renderCellValue(billing, col)}
                          </TableCell>
                        ))}
                        <TableCell>
                          <div className="flex items-center justify-center gap-1">
                            <Button variant="ghost" size="sm" onClick={() => handleEditClick(billing)} data-testid={`edit-${billing.billing_id}`}>
                              <Pencil className="w-4 h-4 text-blue-500" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteClick(billing)} data-testid={`delete-${billing.billing_id}`}>
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
        </motion.div>
        
        {/* Pagination Controls */}
        {filteredBillings.length > 0 && (
          <motion.div 
            className="flex flex-wrap items-center justify-between gap-4 px-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            {/* Page Size Selector */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Show</span>
              <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
                <SelectTrigger className="w-20 h-8" data-testid="page-size-selector">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZE_OPTIONS.map(size => (
                    <SelectItem key={size} value={String(size)}>{size}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-sm text-muted-foreground">per page</span>
            </div>
            
            {/* Page Navigation */}
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => goToPage(1)} 
                disabled={currentPage === 1}
                data-testid="first-page"
              >
                <ChevronsLeft className="w-4 h-4" />
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => goToPage(currentPage - 1)} 
                disabled={currentPage === 1}
                data-testid="prev-page"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              
              <div className="flex items-center gap-1 px-2">
                <span className="text-sm">Page</span>
                <Input 
                  type="number" 
                  min={1} 
                  max={totalPages} 
                  value={currentPage}
                  onChange={(e) => goToPage(Number(e.target.value))}
                  className="w-16 h-8 text-center"
                  data-testid="page-input"
                />
                <span className="text-sm">of {totalPages}</span>
              </div>
              
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => goToPage(currentPage + 1)} 
                disabled={currentPage === totalPages}
                data-testid="next-page"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => goToPage(totalPages)} 
                disabled={currentPage === totalPages}
                data-testid="last-page"
              >
                <ChevronsRight className="w-4 h-4" />
              </Button>
            </div>
            
            {/* Page Info */}
            <div className="text-sm text-muted-foreground">
              Showing {startIndex + 1}-{Math.min(endIndex, filteredBillings.length)} of {filteredBillings.length}
            </div>
          </motion.div>
        )}
        
        {/* Delete All */}
        {clientBillings.length > 0 && (
          <motion.div 
            className="flex justify-end"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Button 
                variant="outline" 
                className="text-destructive border-destructive hover:bg-destructive hover:text-white rounded-xl"
                onClick={() => setShowDeleteAllDialog(true)}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete All ({clientBillings.length})
              </Button>
            </motion.div>
          </motion.div>
        )}
      </div>
      
      {/* Column Settings Dialog */}
      <Dialog open={showColumnDialog} onOpenChange={setShowColumnDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Column Visibility Settings</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground mb-4">Select which columns to display in the table. Changes will be saved to your preferences.</p>
          <div className="grid grid-cols-2 gap-3">
            {Object.keys(COLUMN_DEFS).map(col => (
              <label key={col} className="flex items-center gap-2 p-2 rounded hover:bg-muted cursor-pointer">
                <Checkbox 
                  checked={tempVisibleColumns.includes(col)} 
                  onCheckedChange={() => toggleColumn(col)}
                />
                <span className="text-sm">{COLUMN_DEFS[col].label}</span>
              </label>
            ))}
          </div>
          <div className="flex gap-2 justify-end mt-4">
            <Button variant="outline" onClick={() => setTempVisibleColumns(DEFAULT_VISIBLE)}>Reset to Default</Button>
            <Button variant="outline" onClick={() => setShowColumnDialog(false)}>Cancel</Button>
            <Button onClick={saveColumnSettings}>Save Settings</Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Billing Record</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>PO Number</Label>
                <Input value={editForm.po_number || ''} onChange={(e) => setEditForm({...editForm, po_number: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>PO Status</Label>
                <Select value={editForm.po_status || 'issued'} onValueChange={(v) => setEditForm({...editForm, po_status: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="issued">Issued</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="accepted">Accepted</SelectItem>
                    <SelectItem value="canceled">Canceled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Amount</Label>
                <Input type="number" step="0.01" value={editForm.amount || ''} onChange={(e) => setEditForm({...editForm, amount: parseFloat(e.target.value) || 0})} />
              </div>
              <div className="space-y-2">
                <Label>Qty</Label>
                <Input type="number" step="0.01" value={editForm.qty || ''} onChange={(e) => setEditForm({...editForm, qty: parseFloat(e.target.value) || 0})} />
              </div>
              <div className="space-y-2">
                <Label>Rate</Label>
                <Input type="number" step="0.01" value={editForm.rate || ''} onChange={(e) => setEditForm({...editForm, rate: parseFloat(e.target.value) || 0})} />
              </div>
              <div className="space-y-2">
                <Label>Currency</Label>
                <Select value={editForm.currency || 'USD'} onValueChange={(v) => setEditForm({...editForm, currency: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                    <SelectItem value="GBP">GBP</SelectItem>
                    <SelectItem value="INR">INR</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Client</Label>
                <Input value={editForm.client || ''} onChange={(e) => setEditForm({...editForm, client: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Main Title</Label>
                <Input value={editForm.main_title || ''} onChange={(e) => setEditForm({...editForm, main_title: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Task</Label>
                <Input value={editForm.task || ''} onChange={(e) => setEditForm({...editForm, task: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Due On</Label>
                <Input type="date" value={editForm.due_on?.split('T')[0] || ''} onChange={(e) => setEditForm({...editForm, due_on: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Accepted On</Label>
                <Input type="date" value={editForm.accepted_on?.split('T')[0] || ''} onChange={(e) => setEditForm({...editForm, accepted_on: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Acceptance Status</Label>
                <Select value={editForm.resource_acceptance_status || 'pending'} onValueChange={(v) => setEditForm({...editForm, resource_acceptance_status: v === 'pending' ? '' : v})}>
                  <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Accepted">Accepted</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
              <Button type="submit">Save</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Delete Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              Delete Record
            </DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground">Are you sure you want to delete this record?</p>
          {deletingBilling && (
            <div className="p-4 bg-muted rounded-lg">
              <p className="font-medium">{deletingBilling.main_title || deletingBilling.po_number}</p>
              <p className="text-sm text-muted-foreground">{deletingBilling.client}</p>
              <p className="text-sm font-semibold text-success">{formatUSD(deletingBilling.amount || 0)}</p>
            </div>
          )}
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteConfirm}>Delete</Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Delete All Dialog */}
      <Dialog open={showDeleteAllDialog} onOpenChange={setShowDeleteAllDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              Delete All Records
            </DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground">
            Delete all <strong>{clientBillings.length}</strong> billing records? This cannot be undone!
          </p>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setShowDeleteAllDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteAllConfirm}>Delete All</Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Floating Filter Panel */}
      <FloatingFilterPanel onFilterChange={setDateFilter} showDateFieldSelector={true} />
    </DashboardLayout>
  );
};

export default ClientBillings;
